import { useState, useEffect, useCallback } from "react";
import { Button } from "../shared/button";
import { Spinner } from "../shared/spinner";
import { PreviewModal } from "./preview-modal";
import { IntegrityAlerts } from "./integrity-alerts";
import { ConfirmDialog } from "../shared/confirm-dialog";
import { useCatalog } from "../../hooks/use-catalog";
import { useIntegrity } from "../../hooks/use-integrity";
import { apiPost, apiGet, endpoints } from "../../api/client";
import type { MerchantSettings, JobResponse } from "../../api/types";
import "./inflate-panel.css";

function fmt(cents: number): string {
  return new Intl.NumberFormat("en-US", {
    style: "currency",
    currency: "USD",
  }).format(cents / 100);
}

function applyInflate(
  baseCents: number,
  method: "pct" | "flat",
  pct: number,
  flatCents: number,
  rounding: "none" | "nearest" | "up",
): number {
  let price =
    method === "flat"
      ? baseCents + flatCents
      : Math.round(baseCents / (1 - pct / 100));
  if (rounding === "nearest") price = Math.round(price / 100) * 100;
  else if (rounding === "up") price = Math.ceil(price / 100) * 100;
  return Math.max(price, baseCents);
}

interface InflatePanelProps {
  settings: MerchantSettings;
  onSettingsChange: () => void;
}

export function InflatePanel({ settings, onSettingsChange }: InflatePanelProps) {
  const { items, loading: catalogLoading, reload: reloadCatalog } = useCatalog();
  const { latest: integrityLatest, fetchLatest: fetchIntegrity } = useIntegrity();

  const [method, setMethod] = useState<"pct" | "flat">(settings.inflate_method);
  const [flatAmount, setFlatAmount] = useState(
    (settings.flat_amount_cents || 25) / 100,
  );
  const [rounding, setRounding] = useState(settings.rounding);
  const [showPreview, setShowPreview] = useState(false);
  const [showRestoreConfirm, setShowRestoreConfirm] = useState(false);
  const [jobLoading, setJobLoading] = useState(false);
  const [jobMessage, setJobMessage] = useState<string | null>(null);

  useEffect(() => {
    if (settings.is_inflated) {
      fetchIntegrity();
    }
  }, [settings.is_inflated, fetchIntegrity]);

  const pollJob = useCallback(
    async (jobId: string): Promise<JobResponse> => {
      return new Promise((resolve, reject) => {
        const timer = setInterval(async () => {
          try {
            const res = await apiGet<JobResponse>(endpoints.job(jobId));
            if (res.status === "completed" || res.status === "failed") {
              clearInterval(timer);
              resolve(res);
            }
          } catch (err) {
            clearInterval(timer);
            reject(err);
          }
        }, 2000);
      });
    },
    [],
  );

  async function handleInflate() {
    setJobLoading(true);
    setJobMessage(null);
    try {
      const res = await apiPost<{ job_id: string }>(endpoints.inflate, {
        fee_rate: settings.fee_rate,
        method,
        flat_amount_cents: method === "flat" ? Math.round(flatAmount * 100) : undefined,
        rounding,
      });
      const job = await pollJob(res.job_id);
      if (job.status === "completed") {
        setJobMessage(
          `${job.result?.updated ?? 0} items inflated in Square.`,
        );
        await reloadCatalog();
        onSettingsChange();
      } else {
        setJobMessage(`Inflate failed: ${job.result?.errors?.[0] ?? "Unknown error"}`);
      }
    } catch (err) {
      setJobMessage(err instanceof Error ? err.message : "Inflate failed");
    } finally {
      setJobLoading(false);
      setShowPreview(false);
    }
  }

  async function handleRestore() {
    setJobLoading(true);
    setJobMessage(null);
    try {
      const res = await apiPost<{ job_id: string }>(endpoints.restore);
      const job = await pollJob(res.job_id);
      if (job.status === "completed") {
        setJobMessage(
          `${job.result?.updated ?? 0} items restored.`,
        );
        await reloadCatalog();
        onSettingsChange();
      } else {
        setJobMessage(`Restore failed: ${job.result?.errors?.[0] ?? "Unknown error"}`);
      }
    } catch (err) {
      setJobMessage(err instanceof Error ? err.message : "Restore failed");
    } finally {
      setJobLoading(false);
    }
  }

  if (settings.mode === 1) {
    return (
      <div className="inv-wrap">
        <div className="card" style={{ textAlign: "center", padding: "28px 20px" }}>
          <div style={{ fontSize: "36px", marginBottom: "12px" }}>&#x1F512;</div>
          <div style={{ fontSize: "17px", fontWeight: 800, marginBottom: "8px" }}>
            Mode 1 Active
          </div>
          <div style={{ fontSize: "13px", color: "var(--ink2)", lineHeight: 1.6, marginBottom: "16px" }}>
            Inventory inflation is only available in <strong>Mode 2</strong>.
          </div>
        </div>
      </div>
    );
  }

  if (jobLoading) {
    return (
      <div className="inv-wrap">
        <div className="card" style={{ textAlign: "center", padding: "32px" }}>
          <Spinner />
          <div style={{ fontSize: "14px", fontWeight: 700, marginTop: "16px" }}>
            Processing...
          </div>
        </div>
      </div>
    );
  }

  const hasInflated = items.some((i) =>
    i.variations.some((v) => v.is_inflated),
  );

  return (
    <div className="inv-wrap">
      {jobMessage && (
        <div className="card" style={{ background: "var(--green-bg)", border: "1px solid var(--green-bd)", color: "var(--green)", fontWeight: 700, textAlign: "center" }}>
          {jobMessage}
        </div>
      )}

      {settings.is_inflated && integrityLatest && (
        <IntegrityAlerts check={integrityLatest} />
      )}

      {hasInflated || settings.is_inflated ? (
        <div className="card">
          <div style={{ background: "var(--green-bg)", border: "1px solid var(--green-bd)", borderRadius: "10px", padding: "14px", marginBottom: "14px", display: "flex", alignItems: "center", gap: "12px" }}>
            <span style={{ fontSize: "28px" }}>&#x2705;</span>
            <div>
              <div style={{ fontSize: "14px", fontWeight: 800, color: "var(--green)" }}>Prices Inflated</div>
              <div style={{ fontSize: "12px", color: "var(--green)", marginTop: "2px" }}>
                {method === "pct" ? `Inflated at ${settings.fee_rate}% fee` : `Inflated at +$${flatAmount.toFixed(2)} flat`}
              </div>
            </div>
          </div>
          <Button variant="red" full style={{ padding: "16px", fontSize: "15px" }} onClick={() => setShowRestoreConfirm(true)}>
            Restore All to Original Prices
          </Button>
        </div>
      ) : (
        <div className="card">
          <div className="clabel">Inflate Method</div>
          <div style={{ display: "flex", gap: "8px", marginBottom: "12px" }}>
            <button className={`method-btn ${method === "pct" ? "method-active" : ""}`} onClick={() => setMethod("pct")}>% Percentage</button>
            <button className={`method-btn ${method === "flat" ? "method-active" : ""}`} onClick={() => setMethod("flat")}>$ Flat Amount</button>
          </div>

          {method === "pct" ? (
            <div style={{ display: "flex", alignItems: "center", gap: "8px", marginBottom: "14px" }}>
              <div style={{ background: "var(--surface2)", border: "1px solid var(--border)", borderRadius: "8px", padding: "8px 14px", display: "flex", alignItems: "center", gap: "6px" }}>
                <span style={{ fontFamily: "var(--mono)", fontSize: "18px", fontWeight: 700 }}>{settings.fee_rate}</span>
                <span style={{ fontWeight: 700, color: "var(--ink2)" }}>%</span>
              </div>
              <span style={{ fontSize: "12px", color: "var(--ink2)" }}>from Settings</span>
            </div>
          ) : (
            <div style={{ display: "flex", alignItems: "center", gap: "8px", marginBottom: "14px" }}>
              <span style={{ fontWeight: 700 }}>$</span>
              <input type="number" min={0.01} max={100} step={0.01} value={flatAmount} onChange={(e) => setFlatAmount(parseFloat(e.target.value) || 0)} style={{ width: "80px", padding: "8px 10px", border: "1px solid var(--border)", borderRadius: "8px", fontFamily: "var(--mono)", fontSize: "15px", fontWeight: 600, textAlign: "right", background: "var(--bg)" }} />
              <span style={{ fontSize: "12px", color: "var(--ink2)" }}>per item</span>
            </div>
          )}

          <div className="clabel">Rounding</div>
          <div style={{ display: "flex", flexDirection: "column", gap: "8px", marginBottom: "16px" }}>
            {(["none", "nearest", "up"] as const).map((val) => (
              <label key={val} style={{ display: "flex", alignItems: "center", gap: "8px", fontSize: "13px", cursor: "pointer" }}>
                <input type="radio" name="inflate-round" value={val} checked={rounding === val} onChange={() => setRounding(val)} style={{ accentColor: "var(--ink)" }} />
                {val === "none" && "No rounding"}
                {val === "nearest" && "Round to nearest dollar"}
                {val === "up" && "Always round up"}
              </label>
            ))}
          </div>

          <div style={{ display: "flex", gap: "8px" }}>
            <Button variant="outline" style={{ flex: 1 }} onClick={() => setShowPreview(true)}>Preview</Button>
            <Button variant="gradient" style={{ flex: 1 }} onClick={handleInflate}>Inflate All</Button>
          </div>
        </div>
      )}

      {!catalogLoading && items.length > 0 && (
        <div className="card">
          <div className="clabel">Catalog Preview — at {settings.fee_rate}% fee</div>
          {items.map((item) =>
            item.variations.map((v) => {
              if (!v.price_cents) return null;
              const base = v.original_price_cents ?? v.price_cents;
              const inflated = applyInflate(base, method, settings.fee_rate, Math.round(flatAmount * 100), rounding);
              return (
                <div key={v.id} style={{ display: "flex", justifyContent: "space-between", padding: "8px 0", borderBottom: "1px solid var(--border)", fontSize: "13px" }}>
                  <div>
                    <div style={{ fontWeight: 600 }}>{item.name}{v.name && v.name !== "Regular" ? ` \u00B7 ${v.name}` : ""}</div>
                    {v.is_inflated && <div style={{ fontSize: "10px", color: "var(--green)" }}>Inflated</div>}
                  </div>
                  <div style={{ textAlign: "right" }}>
                    {v.is_inflated ? (
                      <>
                        <div style={{ textDecoration: "line-through", color: "var(--ink3)", fontFamily: "var(--mono)", fontSize: "11px" }}>{fmt(base)}</div>
                        <div style={{ fontFamily: "var(--mono)", fontWeight: 700, color: "var(--blue)" }}>{fmt(v.price_cents)}</div>
                      </>
                    ) : (
                      <>
                        <div style={{ fontFamily: "var(--mono)", color: "var(--ink3)" }}>{fmt(v.price_cents)}</div>
                        <div style={{ fontFamily: "var(--mono)", color: "var(--blue)", fontSize: "11px" }}>&#x2192; {fmt(inflated)}</div>
                      </>
                    )}
                  </div>
                </div>
              );
            }),
          )}
        </div>
      )}

      <PreviewModal open={showPreview} items={items} method={method} pct={settings.fee_rate} flatCents={Math.round(flatAmount * 100)} rounding={rounding} onConfirm={handleInflate} onCancel={() => setShowPreview(false)} />

      <ConfirmDialog
        open={showRestoreConfirm}
        title="Restore all prices?"
        message="This reverts every item in your Square catalog back to its original (pre-inflation) price."
        confirmLabel="Restore"
        variant="destructive"
        onConfirm={() => {
          setShowRestoreConfirm(false);
          handleRestore();
        }}
        onCancel={() => setShowRestoreConfirm(false)}
      />
    </div>
  );
}
